import { TemplateManagerDialog } from "@/components/mailing/TemplateManagerDialog";
import { GroupList } from "@/components/mailing/GroupList";
import { GroupDetail } from "@/components/mailing/GroupDetail";
import { ImportPreviewDialog } from "@/components/mailing/ImportPreviewDialog";
import { SendMailDialog } from "@/components/mailing/SendMailDialog";
import { SendHistoryDialog } from "@/components/mailing/SendHistoryDialog";
import { GroupFormDialog } from "@/components/mailing/GroupFormDialog";
import { ContactFormDialog } from "@/components/mailing/ContactFormDialog";
import { EmailPreviewDialog } from "@/components/mailing/EmailPreviewDialog";
import { MailingGuideDialog } from "@/components/mailing/MailingGuideDialog";
import { CampaignDashboard } from "@/components/mailing/CampaignDashboard";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, FolderPlus, FileText, BarChart3, HelpCircle } from "lucide-react";
import { useMailing } from "@/hooks/useMailing";

export default function Mailing() {
  const m = useMailing();
  const [guideOpen, setGuideOpen] = useState(false);
  const [campaignsOpen, setCampaignsOpen] = useState(false);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="mailing-page">
      <input
        ref={m.fileInputRef}
        type="file"
        accept=".msg,application/vnd.ms-outlook"
        className="hidden"
        onChange={m.handleImportFile}
        data-testid="mailing-import-input"
      />

      <div className="brand-hero rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <Mail className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Mailing</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Kontaktgruppen verwalten und Rund-E-Mails versenden
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" size="icon" onClick={() => setGuideOpen(true)} data-testid="open-guide-btn" aria-label="Kurzanleitung">
            <HelpCircle className="w-5 h-5" />
          </Button>
          <Button variant="outline" onClick={() => setCampaignsOpen(true)} data-testid="open-campaigns-btn">
            <BarChart3 className="w-4 h-4 mr-2" />
            Kampagnen
          </Button>
          <Button variant="outline" onClick={() => m.setTemplateManagerOpen(true)} data-testid="manage-templates-btn">
            <FileText className="w-4 h-4 mr-2" />
            Vorlagen
          </Button>
          <Button onClick={m.openCreateGroup} data-testid="add-group-btn">
            <FolderPlus className="w-4 h-4 mr-2" />
            Neue Gruppe
          </Button>
        </div>
      </div>

      {m.loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[300px_1fr]">
          <GroupList groups={m.groups} selectedId={m.selectedId} onSelect={m.setSelectedId} />
          <GroupDetail
            group={m.selectedGroup}
            importing={m.importing}
            onImportClick={m.handleImportClick}
            onRename={m.openRenameGroup}
            onAddContact={m.openAddContact}
            onSend={m.openSend}
            onHistory={m.openHistory}
            onDeleteGroup={() => m.setGroupToDelete(m.selectedGroup)}
            onEditContact={m.openEditContact}
            onDeleteContact={m.setContactToDelete}
            onCreateGroup={m.openCreateGroup}
          />
        </div>
      )}

      <ImportPreviewDialog
        open={m.previewOpen}
        onClose={m.closeImportPreview}
        filename={m.previewFilename}
        groupName={m.selectedGroup?.name}
        items={m.previewItems}
        selectedCount={m.previewSelectedCount}
        allSelected={m.allNewSelected}
        onToggleAll={m.toggleSelectAll}
        onToggleItem={m.togglePreviewItem}
        onConfirm={m.handleConfirmImport}
        confirming={m.confirming}
      />

      <GroupFormDialog
        open={m.groupDialogOpen}
        onOpenChange={m.setGroupDialogOpen}
        editMode={m.groupEditMode}
        name={m.groupName}
        setName={m.setGroupName}
        onSubmit={m.handleSaveGroup}
        saving={m.savingGroup}
      />

      <ContactFormDialog
        open={m.contactDialogOpen}
        onOpenChange={m.setContactDialogOpen}
        editMode={!!m.editContact}
        name={m.contactName}
        setName={m.setContactName}
        email={m.contactEmail}
        setEmail={m.setContactEmail}
        salutation={m.contactSalutation}
        setSalutation={m.setContactSalutation}
        firstName={m.contactFirstName}
        setFirstName={m.setContactFirstName}
        lastName={m.contactLastName}
        setLastName={m.setContactLastName}
        important={m.contactImportant}
        setImportant={m.setContactImportant}
        onSubmit={m.handleSaveContact}
        saving={m.savingContact}
      />

      <SendMailDialog
        open={m.sendDialogOpen}
        onOpenChange={m.setSendDialogOpen}
        group={m.selectedGroup}
        contactsCount={m.contacts.length}
        templates={m.templates}
        selectedTemplate={m.selectedTemplate}
        onApplyTemplate={m.applyTemplate}
        subject={m.sendSubject}
        setSubject={m.setSendSubject}
        blocks={m.sendBlocks}
        setBlocks={m.setSendBlocks}
        heroImage={m.sendHero}
        setHeroImage={m.setSendHero}
        preheader={m.sendPreheader}
        setPreheader={m.setSendPreheader}
        hasContent={m.hasContent}
        onSubmit={m.handleSend}
        sending={m.sending}
        onSendTest={m.handleSendTest}
        sendingTest={m.sendingTest}
        onPreview={m.handlePreviewHtml}
        loadingPreview={m.loadingPreview}
      />

      <SendHistoryDialog
        open={m.historyOpen}
        onOpenChange={m.setHistoryOpen}
        groupName={m.selectedGroup?.name}
        loading={m.loadingHistory}
        history={m.history}
      />

      <TemplateManagerDialog
        open={m.templateManagerOpen}
        onOpenChange={m.setTemplateManagerOpen}
        templates={m.templates}
        onChanged={m.fetchTemplates}
      />

      <EmailPreviewDialog open={m.previewHtmlOpen} onOpenChange={m.setPreviewHtmlOpen} html={m.previewHtmlContent} blocks={m.sendBlocks} subject={m.sendSubject} />

      <MailingGuideDialog open={guideOpen} onOpenChange={setGuideOpen} />
      <CampaignDashboard open={campaignsOpen} onOpenChange={setCampaignsOpen} />

      {/* Delete group confirm */}
      <AlertDialog open={!!m.groupToDelete} onOpenChange={(o) => !o && m.setGroupToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gruppe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{m.groupToDelete?.name}" und alle enthaltenen Kontakte werden gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={m.handleDeleteGroup} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-group-btn">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete contact confirm */}
      <AlertDialog open={!!m.contactToDelete} onOpenChange={(o) => !o && m.setContactToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kontakt entfernen?</AlertDialogTitle>
            <AlertDialogDescription>
              „{m.contactToDelete?.email}" wird aus der Gruppe entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={m.handleDeleteContact} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-contact-btn">
              Entfernen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
